/**
 * Prix enregistrés en CAD, affichés dans la devise choisie (Profil → Format) avec le taux du jour
 * de la Banque du Canada (GET /config/exchange-rates/). Sans taux disponible, le prix reste en CAD.
 */

import { useSyncExternalStore } from 'react'
import { currentLocale } from '../../i18n'
import { getApiBaseUrl, getApiVersion } from '../api/runtimeConfig'
import { getFormat, type Currency } from './preferences'

export type ExchangeRates = { base: 'CAD'; date: string | null; rates: Partial<Record<Currency, number>>; stale?: boolean }

const STORAGE_KEY = 'exchange_rates'
const MAX_AGE_MS = 12 * 3600 * 1000
const listeners = new Set<() => void>()
let rates: ExchangeRates | null = readStored()
let loading: Promise<void> | null = null

function readStored(): ExchangeRates | null {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') as (ExchangeRates & { savedAt?: number }) | null
    return raw?.rates ? raw : null
  } catch {
    return null
  }
}

function storedAge(): number {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') as { savedAt?: number } | null
    return raw?.savedAt ? Date.now() - raw.savedAt : Infinity
  } catch {
    return Infinity
  }
}

/** Charge les taux si la devise affichée n'est pas le CAD et qu'ils datent de plus de 12 h. */
export function ensureExchangeRates(): Promise<void> {
  if (getFormat().currency === 'CAD' || loading || (rates && storedAge() < MAX_AGE_MS)) return loading ?? Promise.resolve()
  loading = fetch(`${getApiBaseUrl()}/${getApiVersion()}/config/exchange-rates/`)
    .then((r) => (r.ok ? (r.json() as Promise<ExchangeRates>) : null))
    .then((data) => {
      if (!data?.rates) return
      rates = data
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, savedAt: Date.now() }))
      } catch {
        // stockage indisponible
      }
      listeners.forEach((listener) => listener())
    })
    .catch(() => undefined)
    .finally(() => {
      loading = null
    })
  return loading
}

export function getExchangeRates(): ExchangeRates | null {
  return rates
}

export function setExchangeRatesForTests(value: ExchangeRates | null) {
  rates = value
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Taux courants ; le composant se met à jour quand ils arrivent. */
export function useExchangeRates(): ExchangeRates | null {
  return useSyncExternalStore(subscribe, getExchangeRates, getExchangeRates)
}

/** Montant en CAD → montant dans la devise choisie, ou null si le taux manque. */
export function convertFromCad(amountCad: number, currency: Currency = getFormat().currency): number | null {
  if (currency === 'CAD') return amountCad
  const rate = rates?.rates[currency]
  return rate ? amountCad * rate : null
}

/** ``approx: false`` retire le « ≈ » (fourchette : un seul « ≈ » devant). */
export type PriceOptions = { decimals?: number; currency?: Currency; approx?: boolean }

function formatIn(amount: number, currency: Currency, decimals?: number): string {
  const digits = currency === 'JPY' ? 0 : decimals ?? (Number.isInteger(amount) ? 0 : 2)
  return new Intl.NumberFormat(currentLocale(), {
    style: 'currency',
    currency,
    currencyDisplay: currency === 'CAD' || currency === 'USD' || currency === 'AUD' || currency === 'MXN' ? 'symbol' : 'narrowSymbol',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount)
}

/** Prix en CAD dans la devise choisie : « 25 $ » ou « ≈ 17,57 $ US ». Sans taux : en CAD. */
export function formatPrice(amountCad: number, options: PriceOptions = {}): string {
  const currency = options.currency ?? getFormat().currency
  const converted = convertFromCad(amountCad, currency)
  if (converted === null || currency === 'CAD') return formatIn(amountCad, 'CAD', options.decimals)
  const text = formatIn(converted, currency, options.decimals ?? 2)
  return options.approx === false ? text : `≈ ${text}`
}

/** Fourchette de prix en CAD : « 20 $ – 35 $ » ou « ≈ 14,06 $ US – 24,60 $ US ». */
export function formatPriceRange(minCad: number, maxCad: number, join: (min: string, max: string) => string, options: PriceOptions = {}): string {
  const min = formatPrice(minCad, { ...options, approx: false })
  const max = formatPrice(maxCad, { ...options, approx: false })
  if (min === max) return formatPrice(minCad, options)
  const text = join(min, max)
  return pricesAreConverted() && options.approx !== false ? `≈ ${text}` : text
}

/** Prix d'origine en CAD (infobulle sous un prix converti). */
export function formatPriceCad(amountCad: number, decimals?: number): string {
  return formatIn(amountCad, 'CAD', decimals)
}

/** Vrai si les prix s'affichent convertis (devise ≠ CAD et taux disponible). */
export function pricesAreConverted(): boolean {
  const currency = getFormat().currency
  return currency !== 'CAD' && convertFromCad(1, currency) !== null
}
